@if (showSkeleton) {
  <div
    class="skeleton-box-card"
    role="status"
    [attr.aria-label]="'Loading survey'"
    [attr.aria-busy]="true"
  >
    @for (ghostElement of ghostElements; track $index) {
      <div
        class="skeleton-box-card"
        role="presentation"
        aria-hidden="true"
      >
        <div class="row">
          <div class="col-12">
            <div class="d-flex stats-card shadow">
              <div class="skeleton-animation w-100 d-flex justify-content-center flex-column gap-3">
                <div class="bar bar-1"></div>
                <div class="bar bar-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  </div>
}
<div
  class="survey-app"
  role="main"
  [attr.aria-label]="'Survey'"
  [attr.aria-describedby]="'survey-instructions'"
>
  <div
    id="survey-instructions"
    class="visually-hidden"
    role="status"
  >
    This is a multi-step survey. Use the navigation buttons to move between steps. Required fields are marked with an asterisk (*).
  </div>
  <mat-horizontal-stepper
    #stepper
    [disableRipple]="true"
    class="pt-4"
    [ngStyle]="{ 'd-none': showSkeleton, 'd-block': !showSkeleton }"
    [linear]="showCustomForm"
    (selectionChange)="onStepChanged($event)"
    role="region"
    [attr.aria-label]="'Survey steps'"
    [attr.aria-orientation]="'horizontal'"
  >
    <!-- Completed steps keep their number: the default edit/done icons are Material Icons ligatures, and that font is not loaded -->
    <ng-template matStepperIcon="edit" let-index="index">
      <span aria-hidden="true">{{ index + 1 }}</span>
    </ng-template>
    <ng-template matStepperIcon="done" let-index="index">
      <span aria-hidden="true">{{ index + 1 }}</span>
    </ng-template>
    @for (surveyStep of surveyService?.surveySteps; track surveyStep; let last = $last; let index = $index) {
      <form
        #stepperForm="ngForm"
        role="form"
        [attr.aria-label]="'Step ' + (index + 1) + ' of ' + surveyService?.surveySteps.length"
      >
        <mat-step
          [completed]="!showCustomForm"
          [aria-label]="surveyStep | translation : 'title' : lang"
          [attr.aria-selected]="stepper.selectedIndex === index"
        >
          <ng-template matStepLabel>
            {{ surveyStep | translation : "title" : lang }}
          </ng-template>
          <div
            class="mt-4"
            role="region"
            [attr.aria-label]="'Step content'"
          >
            <div>
              <app-csw-text
                [text]="surveyStep.is_start === 1 ? (surveyStep | translation : 'title' : lang) : surveyStep.step_number + ' - ' + (surveyStep | translation : 'title' : lang)"
                [styleConfig]="{ fontWeight: 600, fontSize: '28px' }"
                role="heading"
                [attr.aria-level]="1"
              />
            </div>
            <div
              class="mt-3"
              role="doc-subtitle"
            >
              <app-csw-text
                [text]="surveyStep | translation : 'description' : lang"
              />
            </div>
          </div>
          <div
            class="mt-5"
            role="region"
            [attr.aria-label]="'Step form'"
          >
            @if (showErrorMessage) {
              <div
                class="alert alert-danger"
                role="alert"
                [attr.aria-live]="'assertive'"
                [attr.aria-atomic]="true"
              >
                {{ errorMessage | translate }}
              </div>
            }
            @if (surveyStep.is_start === 1 && (widgetService?.widgetConf | translation : 'start_text' : lang)?.trim()) {
              <app-csw-text
                [text]="widgetService?.widgetConf | translation : 'start_text' : lang | calioSafe : 'html'"
                role="doc-introduction"
              />

              @if (showCustomForm) {
                <div
                  class="mt-3"
                  role="region"
                  [attr.aria-label]="'Customer information form'"
                >
                  <app-survey-customer-form />
                </div>
              }
            }
            @if (surveyStep.is_end === 1) {
              <app-survey-end-step />
            }
            @if (surveyStep.is_start !== 1 && surveyStep.is_end !== 1) {
              @if (showStepperContent) {
                @if (surveyStep.is_appointment_booking_step === 0) {
                  <app-survey-questions-view [currentSurveyStep]="surveyStep" />
                }
                @if (surveyStep.is_appointment_booking_step === 1) {
                  @if (showFinalStep) {
                    <app-survey-final-step
                      (endProcessEvent)="onEndProcessEvent(); goForward(stepper, surveyStep)"
                      (bookAppointmentEvent)="onBookAppointmentEvent()"
                    />
                  }
                  <div
                    [ngStyle]="{ display: showBookingWidget ? 'block' : 'none' }"
                    role="region"
                    [attr.aria-label]="'Booking widget'"
                  >
                    <app-survey-integrate-booking-widget />
                  </div>
                }
              }
            }
          </div>
          <div
            class="cbw-survey-buttons"
            role="navigation"
            [attr.aria-label]="'Step navigation'"
            [ngStyle]="{ display: (surveyStep.is_appointment_booking_step === 1 && showBookingWidget && !isBookingCompleted) ? 'none' : 'flex' }"
          >
            @if (surveyStep.is_start !== 1) {
              <div class="pe-2">
                @if (!last) {
                  <button
                    type="button"
                    class="btn btn-primary"
                    [ngStyle]="{
                      background: widgetService?.widgetConf?.button_background_color,
                      color: widgetService?.widgetConf?.button_font_color,
                      borderColor: widgetService?.widgetConf?.button_background_color,
                      borderRadius: '0px'
                    }"
                    (click)="goBackward(stepper)"
                    role="button"
                    [attr.aria-label]="'Previous step: ' + (surveyService?.surveySteps[index - 1] | translation : 'title' : lang)"
                  >
                    <div class="d-flex align-items-center">
                      @if (surveyStep.is_start !== 1) {
                        <div class="pe-3">
                          <mat-icon
                            class="button-icon d-flex"
                            svgIcon="chevron-left-solid"
                            aria-hidden="true"
                          />
                        </div>
                      }
                      <div>
                        @if (surveyStep.is_start !== 1) {
                          <app-csw-text
                            [text]="('form_fields.previous' | translate) + ': ' + (surveyService?.surveySteps[index - 1] | translation : 'title' : lang)"
                            customClass="fs-14"
                          />
                        }
                      </div>
                    </div>
                  </button>
                }
              </div>
            }
            <div>
              @if (!last) {
                <button
                  type="button"
                  class="btn btn-primary"
                  [disabled]="handleNextButtonState(surveyStep, stepperForm.invalid)"
                  [ngStyle]="{
                    background: widgetService?.widgetConf?.button_background_color,
                    color: widgetService?.widgetConf?.button_font_color,
                    borderColor: widgetService?.widgetConf?.button_background_color,
                    borderRadius: '0px'
                  }"
                  (click)="goForward(stepper, surveyStep)"
                  role="button"
                  [attr.aria-label]="surveyStep.is_start === 1 ? 'Start survey' : 'Next step: ' + (surveyService?.surveySteps[index + 1] | translation : 'title' : lang)"
                  [attr.aria-disabled]="handleNextButtonState(surveyStep, stepperForm.invalid)"
                >
                  <div class="d-flex align-items-center">
                    <div>
                      @if (surveyStep.is_start === 1) {
                        <app-csw-text
                          [text]="'form_fields.start' | translate"
                          customClass="fs-14"
                        />
                      }
                      @if (surveyStep.is_start !== 1) {
                        <app-csw-text
                          [text]="('form_fields.next' | translate) + ': ' + (surveyService?.surveySteps[index + 1] | translation : 'title' : lang)"
                          customClass="fs-14"
                        />
                      }
                    </div>
                    @if (surveyStep.is_start !== 1) {
                      <div class="ps-3">
                        <mat-icon
                          class="button-icon d-flex"
                          svgIcon="chevron-right-solid"
                          aria-hidden="true"
                        />
                      </div>
                    }
                  </div>
                </button>
              }
            </div>
          </div>
        </mat-step>
      </form>
    }
  </mat-horizontal-stepper>
</div>
@if (surveyService?.partnerData?.analytics?.GTM_ENABLED
     && surveyService?.partnerData?.analytics?.GTM
     && surveyService?.partnerData?.analytics?.GTM.trim() !== '') {
  <div role="complementary" aria-hidden="true">
    <app-gtm-operation [gtmID]="surveyService?.partnerData?.analytics?.GTM" />
  </div>
}
